import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Copy, ExternalLink, CheckCircle2, AlertCircle, Calendar, Clock,
  ChevronDown, ChevronRight, Play, FileText, Info, AlertTriangle,
  CheckSquare, Square, ArrowRight, Zap
} from 'lucide-react';

/**
 * RichMessage - Renders markdown with interactive elements:
 * - Markdown: headers, lists, code blocks, tables, blockquotes
 * - [button:Label:action:params] - Clickable action buttons
 * - [task:Description] - Task cards with checkbox
 * - [calendar:Event:Date] - Calendar event cards
 * - [info:message], [warning:message], [success:message] - Alert boxes
 * - [progress:percent:message] - Visual progress bars
 * - [link:Title:URL:Description] - Link preview cards
 * - [collapse:Title]content[/collapse] - Collapsible sections
 */
const RichMessage = ({ content, className, onAction }) => {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [checkedTasks, setCheckedTasks] = useState({});
  
  const handleAction = (action, params) => {
    if (onAction) { onAction(action, params); return; }
    switch (action) {
      case 'navigate': window.location.href = params; break;
      case 'copy': navigator.clipboard.writeText(params); toast.success('Copied'); break;
      case 'open': window.open(params, '_blank'); break;
      default: toast.info(`Action: ${action}`);
    }
  };
  
  const toggleSection = (id) => setCollapsedSections(p => ({ ...p, [id]: !p[id] }));
  const toggleTask = (id) => setCheckedTasks(p => ({ ...p, [id]: !p[id] }));

  if (!content) return null;

  // Split content into segments: interactive blocks and markdown text
  const segments = [];
  let remaining = content;
  
  // Pattern for interactive blocks
  const blockPattern = /\[(button|task|calendar|progress|info|warning|success|link|collapse):([^\]]*)\](?:([\s\S]*?)\[\/\1\])?/g;
  let match;
  let lastIndex = 0;
  
  const tempStr = remaining;
  while ((match = blockPattern.exec(tempStr)) !== null) {
    // Add preceding text as markdown
    if (match.index > lastIndex) {
      segments.push({ type: 'markdown', content: tempStr.slice(lastIndex, match.index) });
    }
    
    const [, blockType, params, innerContent] = match;
    segments.push({ type: blockType, params, innerContent: innerContent || '' });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < tempStr.length) {
    segments.push({ type: 'markdown', content: tempStr.slice(lastIndex) });
  }
  
  // If no interactive blocks found, render as pure markdown
  if (segments.length === 0 || (segments.length === 1 && segments[0].type === 'markdown')) {
    return <MarkdownRenderer content={content} className={className} />;
  }

  return (
    <div className={cn("space-y-2", className)}>
      {segments.map((seg, i) => {
        if (seg.type === 'markdown') {
          return <MarkdownRenderer key={i} content={seg.content} className="" />;
        }
        
        const parts = seg.params.split(':');
        
        switch (seg.type) {
          case 'button': {
            const [label, action, ...rest] = parts;
            const actionParams = rest.join(':');
            return (
              <Button key={i} size="sm" variant="outline" 
                className="mr-2 mb-1 border-violet-200 text-violet-700 hover:bg-violet-50"
                onClick={() => handleAction(action, actionParams)}>
                <Zap className="w-3 h-3 mr-1" />{label}
              </Button>
            );
          }
          
          case 'task': {
            const taskId = `task-${i}`;
            const checked = checkedTasks[taskId];
            return (
              <div key={i} onClick={() => toggleTask(taskId)}
                className={cn("flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer orka-transition-colors",
                  checked ? "bg-emerald-50 border-emerald-200 line-through text-muted-foreground" : "bg-slate-50 border-slate-200 hover:border-violet-300")}>
                {checked ? <CheckSquare className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> 
                         : <Square className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
                <span className="text-sm">{parts.join(':')}</span>
              </div>
            );
          }
          
          case 'calendar': {
            const [event, ...dateParts] = parts;
            return (
              <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200">
                <Calendar className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div>
                  <span className="text-sm font-medium text-blue-900">{event}</span>
                  <span className="text-xs text-blue-600 ml-2">{dateParts.join(':')}</span>
                </div>
              </div>
            );
          }
          
          case 'progress': {
            const [percent, ...msgParts] = parts;
            const pct = parseInt(percent) || 0;
            return (
              <div key={i} className="p-2.5 rounded-lg bg-slate-50 border">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">{msgParts.join(':') || 'Progress'}</span>
                  <span className="text-xs text-muted-foreground">{pct}%</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className={cn("h-full rounded-full orka-transition-colors", pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-blue-500" : "bg-amber-500")}
                    style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
              </div>
            );
          }
          
          case 'info':
            return (
              <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-blue-900">{parts.join(':')}</span>
              </div>
            );
          
          case 'warning':
            return (
              <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-amber-900">{parts.join(':')}</span>
              </div>
            );
          
          case 'success':
            return (
              <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-emerald-900">{parts.join(':')}</span>
              </div>
            );
          
          case 'link': {
            const [title, url, ...descParts] = parts;
            return (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border hover:border-violet-300 hover:bg-violet-50/50 orka-transition-colors group">
                <div className="w-8 h-8 rounded bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <ExternalLink className="w-4 h-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-violet-700 group-hover:underline truncate">{title}</div>
                  {descParts.length > 0 && <div className="text-xs text-muted-foreground truncate">{descParts.join(':')}</div>}
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0" />
              </a>
            );
          }
          
          case 'collapse': {
            const sectionId = `section-${i}`;
            const isOpen = !collapsedSections[sectionId];
            return (
              <div key={i} className="rounded-lg border">
                <button onClick={() => toggleSection(sectionId)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium hover:bg-muted/50 transition-colors">
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {parts.join(':')}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 border-t">
                    <MarkdownRenderer content={seg.innerContent} className="pt-2" />
                  </div>
                )}
              </div>
            );
          }
          
          default:
            return null;
        }
      })}
    </div>
  );
};

// Extracted markdown renderer
// Linkify task IDs (T1A2B3C4 / task_abc12345) so they become clickable.
// Clicking dispatches a global `orka:open-task` CustomEvent — any open page
// (TaskList, OrganisationPage etc.) can listen and open its edit modal.
const TASK_ID_RE = /\b(T[A-Fa-f0-9]{6,12}|task_[a-f0-9]{6,16})\b/g;

const TaskIdLink = ({ taskId }) => (
  <a
    href={`#/tasks/${taskId}`}
    className="font-mono text-[0.95em] text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 underline decoration-dotted underline-offset-2"
    data-testid={`task-id-link-${taskId}`}
    onClick={(e) => {
      e.preventDefault();
      try {
        window.dispatchEvent(new CustomEvent('orka:open-task', { detail: { task_id: taskId } }));
      } catch { /* noop */ }
    }}
    title={`Open ${taskId}`}
  >
    {taskId}
  </a>
);

const linkifyChildren = (children) => {
  return React.Children.map(children, (child, idx) => {
    if (typeof child !== 'string') return child;
    const parts = [];
    let last = 0;
    let m;
    TASK_ID_RE.lastIndex = 0;
    while ((m = TASK_ID_RE.exec(child)) !== null) {
      if (m.index > last) parts.push(child.slice(last, m.index));
      parts.push(<TaskIdLink key={`tid-${idx}-${m.index}`} taskId={m[0]} />);
      last = m.index + m[0].length;
    }
    if (last === 0) return child;
    if (last < child.length) parts.push(child.slice(last));
    return parts;
  });
};

const MarkdownRenderer = ({ content, className }) => (
  <div className={cn("prose prose-sm max-w-none dark:prose-invert", className)}>
    <ReactMarkdown
      components={{
        p: ({ children }) => {
          const hasBlock = React.Children.toArray(children).some(
            c => typeof c === 'object' && c?.type && ['pre', 'div', 'table', 'blockquote', 'ul', 'ol', 'h1', 'h2', 'h3'].includes(c.type)
          );
          const linked = linkifyChildren(children);
          return hasBlock 
            ? <div className="mb-2 last:mb-0 leading-relaxed">{linked}</div>
            : <p className="mb-2 last:mb-0 leading-relaxed">{linked}</p>;
        },
        ul: ({ children }) => <ul className="my-2 ml-4 list-disc space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="my-2 ml-4 list-decimal space-y-1">{children}</ol>,
        li: ({ children }) => <li className="my-0.5">{linkifyChildren(children)}</li>,
        strong: ({ children }) => <strong className="font-semibold">{linkifyChildren(children)}</strong>,
        em: ({ children }) => <em className="italic">{linkifyChildren(children)}</em>,
        code: ({ inline, children, className: codeClassName }) => {
          const match = /language-(\w+)/.exec(codeClassName || '');
          const language = match ? match[1] : '';
          if (inline) {
            return <code className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono text-violet-600 dark:text-violet-400">{children}</code>;
          }
          return (
            <div className="relative group my-3">
              <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <Button variant="ghost" size="icon" className="h-7 w-7 bg-slate-800/80 hover:bg-slate-700"
                  onClick={() => { navigator.clipboard.writeText(String(children)); toast.success('Copied!'); }}>
                  <Copy className="w-3 h-3 text-white" />
                </Button>
              </div>
              {language && <div className="absolute left-3 top-2 text-[10px] text-slate-400 font-mono uppercase">{language}</div>}
              <pre className="p-4 pt-8 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto text-sm font-mono leading-relaxed">
                <code className="text-slate-100">{children}</code>
              </pre>
            </div>
          );
        },
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" 
            className="text-violet-600 dark:text-violet-400 hover:underline inline-flex items-center gap-1 font-medium">
            {children}<ExternalLink className="w-3 h-3" />
          </a>
        ),
        strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-3 border-violet-400 pl-4 py-1 my-3 bg-violet-50/50 dark:bg-violet-950/20 rounded-r-lg">{children}</blockquote>
        ),
        table: ({ children }) => (
          <div className="my-3 overflow-x-auto rounded-lg border border-border"><table className="w-full text-sm">{children}</table></div>
        ),
        thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
        tbody: ({ children }) => <tbody className="divide-y divide-border">{children}</tbody>,
        th: ({ children }) => <th className="px-4 py-2.5 text-left font-semibold text-foreground bg-muted">{children}</th>,
        td: ({ children }) => <td className="px-4 py-2.5">{children}</td>,
        hr: () => <hr className="my-4 border-border" />,
        h1: ({ children }) => <h1 className="text-xl font-bold mt-5 mb-3 text-foreground pb-2 border-b">{children}</h1>,
        h2: ({ children }) => <h2 className="text-lg font-bold mt-4 mb-2 text-foreground">{children}</h2>,
        h3: ({ children }) => <h3 className="text-base font-semibold mt-3 mb-2 text-foreground">{children}</h3>,
        h4: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-1 text-foreground">{children}</h4>,
        img: ({ src, alt }) => <img src={src} alt={alt || ''} className="rounded-lg max-w-full my-3 border border-border" />,
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);

export default RichMessage;
